<template>
  <Popup v-model="showPop" position="bottom" @input="onPopupInput">
    <div class="payment-popup">
      <div class="payment-popup__close" @click="closePopup"><img src="@/assets/img/close.png" alt=""></div>

      <div class="payment-popup__title">{{ $t('kycPop.paymentTitle') }}</div>
      <div class="payment-popup__desc">{{ $t('kycPop.paymentDesc') }}</div>

      <div class="payment-popup__amount">{{ priceUint }}{{ amount }}</div>

      <div class="payment-popup__countdown">
        {{ $t('kycPop.paymentExpiration') }} {{ countdownText }}
      </div>

      <div class="payment-popup__qr" ref="qrContainer">
        <canvas ref="qrCanvas" class="qr-canvas"></canvas>
      </div>

      <div class="payment-popup__steps">
        <div class="payment-popup__step">step1: {{ $t('kycPop.paymentStep1') }}</div>
        <div class="payment-popup__step">step2: {{ $t('kycPop.paymentStep2') }}</div>
        <div class="payment-popup__step">step3: {{ $t('kycPop.paymentStep3') }}</div>
      </div>

      <div class="payment-popup__footer">
        <div class="btn-primary" @click="saveQR">{{ $t('kycPop.saveQR') }}</div>
      </div>
    </div>
  </Popup>
</template>

<script>
import { Popup } from 'vant';
import QRCode from 'qrcode';
import { getPaymentRemainingSeconds } from '@/utils/date';

export default {
  name: 'PaymentPopup',
  components: {
    Popup
  },
  data() {
    return {
      showPop: false,
      payContent: '',
      amount: '',
      createTime: '',
      countdown: 30 * 60,
      timer: null,
      countdownText: '30:00',
      closeEmitted: false
    }
  },
  computed: {
    priceUint() {
      return this.$t('priceUnit');
    }
  },
  methods: {
    open({ payContent, amount, createTime }) {
      this.payContent = payContent;
      this.amount = amount;
      this.createTime = createTime || '';
      this.closeEmitted = false;
      this.showPop = true;

      // 计算倒计时：从服务端 createTime 开始 30 分钟
      if (this.createTime) {
        this.countdown = getPaymentRemainingSeconds(this.createTime);
      } else {
        this.countdown = 30 * 60;
      }

      this.startCountdown();
      this.$nextTick(() => {
        this.generateQRCode();
      });
    },
    closePopup() {
      this.showPop = false;
      this.finishClose();
    },
    onPopupInput(value) {
      this.showPop = value;
      if (!value) this.finishClose();
    },
    finishClose() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      if (!this.closeEmitted) {
        this.closeEmitted = true;
        this.$emit('close');
      }
    },
    close() {
      this.closePopup();
    },
    startCountdown() {
      this.endTime = Date.now() + this.countdown * 1000;
      this.updateCountdownText();
      this.scheduleTick();
    },
    scheduleTick() {
      const remaining = Math.max(0, Math.floor((this.endTime - Date.now()) / 1000));
      if (this.countdown !== remaining) {
        this.countdown = remaining;
        this.updateCountdownText();
      }
      if (remaining <= 0) {
        this.$toast(this.$t('kycPop.paymentExpired'));
        return;
      }
      const now = Date.now();
      const nextMs = 1000 - (now % 1000) + 1;
      this.timer = setTimeout(() => this.scheduleTick(), nextMs);
    },
    updateCountdownText() {
      const minutes = Math.floor(this.countdown / 60);
      const seconds = this.countdown % 60;
      this.countdownText = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },
    generateQRCode() {
      if (!this.payContent) return;

      const canvas = this.$refs.qrCanvas;
      if (!canvas) return;

      QRCode.toCanvas(canvas, this.payContent, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      }, (error) => {
        if (error) {
          console.error('QR code generation failed:', error);
        }
      });
    },
    saveQR() {
      const canvas = this.$refs.qrCanvas;
      if (!canvas) return;

      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'payment-qr.png';
      link.href = url;
      link.click();
    }
  },
  beforeDestroy() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
</script>

<style lang="less">
.payment-popup {
  padding: 40px 30px 50px;
  width: 750px;
  background: #FFFFFF;
  border-radius: 50px 50px 0px 0px;
  position: relative;

  &__close {
    position: absolute;
    right: 22px;
    top: 28px;
    width: 40px;
    height: 40px;

    img {
      margin: 8px auto;
      display: block;
      width: 24px;
      height: 24px;
    }
  }

  &__title {
    font-size: 36px;
    font-weight: 600;
    line-height: 50px;
    color: var(--text-color-black);
    text-align: left;
  }

  &__desc {
    margin-top: 16px;
    font-size: 26px;
    line-height: 36px;
    color: var(--text-color-gray);
    text-align: left;
  }

  &__amount {
    margin-top: 30px;
    font-size: 40px;
    font-weight: 600;
    line-height: 50px;
    color: var(--red-color);
    text-align: center;
  }

  &__qr {
    margin-top: 30px;
    display: flex;
    justify-content: center;

    .qr-canvas {
      width: 200px;
      height: 200px;
    }
  }

  &__countdown {
    margin-top: 20px;
    font-size: 26px;
    line-height: 36px;
    color: var(--text-color-gray);
    text-align: center;
  }

  &__steps {
    margin-top: 30px;
    padding: 24px;
    background: var(--bg-color-gray);
    border-radius: 16px;
  }

  &__step {
    font-size: 24px;
    line-height: 36px;
    color: var(--text-color-gray);

    &:not(:first-child) {
      margin-top: 12px;
    }
  }

  &__footer {
    margin-top: 30px;
  }
}
</style>
